import { MethodGraphSchema, type MethodGraph } from '../schemas/index.js';
import { DPSE_CONTEXT } from '../vocabulary/index.js';
import { normaliseDocument, type ParseIssue } from './normalise.js';

export type ParseResult =
  | { readonly ok: true; readonly graph: MethodGraph }
  | { readonly ok: false; readonly issues: readonly ParseIssue[] };

export class MethodGraphParseError extends Error {
  readonly issues: readonly ParseIssue[];

  constructor(issues: readonly ParseIssue[]) {
    const first = issues[0];
    const detail = first === undefined ? 'unknown reason' : `${first.path}: ${first.message}`;
    super(
      `method graph is not valid JSON-LD (${String(issues.length)} issue(s)); first: ${detail}`,
    );
    this.name = 'MethodGraphParseError';
    this.issues = issues;
  }
}

/**
 * Parses any DPSE JSON-LD document into a canonical `MethodGraph`.
 *
 * The returned graph always carries `DPSE_CONTEXT`, never the input's context:
 * the input context is checked for conflicts and then discarded, because two
 * spellings of the same ontology must not survive as two in-memory shapes.
 * That is what makes `parse(serialise(g))` deep-equal `g`.
 */
export function safeParseMethodGraph(input: unknown): ParseResult {
  const { nodes, issues } = normaliseDocument(input);
  if (issues.length > 0) return { ok: false, issues };

  const result = MethodGraphSchema.safeParse({ '@context': DPSE_CONTEXT, '@graph': nodes });
  if (!result.success) {
    return {
      ok: false,
      issues: result.error.issues.map((issue) => ({
        path: issue.path.map((segment) => String(segment)).join('.'),
        message: issue.message,
      })),
    };
  }
  return { ok: true, graph: result.data };
}

/** Throwing variant. Use `safeParseMethodGraph` when the issues are user-facing. */
export function parseMethodGraph(input: unknown): MethodGraph {
  const result = safeParseMethodGraph(input);
  if (!result.ok) throw new MethodGraphParseError(result.issues);
  return result.graph;
}

export function parseMethodGraphJson(text: string): MethodGraph {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'invalid JSON';
    throw new MethodGraphParseError([{ path: '', message }]);
  }
  return parseMethodGraph(parsed);
}

export type { ParseIssue };
